import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../../../context/Context";
import { Button, CircleX, SearchCheckList, Select, Send } from "../../ui";
import { fetchAppSendsAPI, fetchSendAppsAPI } from "../../../utils/api/menuAppSend";
import { integrationLabel } from "../../../utils/integrations";
import { menuErrorMessage } from "../menuRole";
import { sendToAppText } from "./sendToAppText";
import { sendToAppPreviewText } from "./sendToAppPreviewText";
import SendToAppPreviewModal from "./SendToAppPreviewModal";

// Selection-mode toolbar of "Send Items to App": action, app picker (one or several apps),
// Send N Items and Cancel. "Send N Items" opens the preview of the e-mail(s) and file(s); from
// there the user sends now (after a confirmation) or saves a draft. Both create Dashboard
// Requests; nothing leaves the system without the confirmation, and the backend re-validates
// menu, items, apps and permission.
export default function SendToAppBar({ menuId, storeId, selectedIds, action, onActionChange, totalCount, availableCount, checking = false, canSend, onSelectAll, onClear, onCancel, onSent, onError }) {
  const { lang } = useContext(Context);
  const navigate = useNavigate();
  const t = sendToAppText(lang);
  const pt = sendToAppPreviewText(lang);
  const [apps, setApps] = useState(null);
  const [selectedApps, setSelectedApps] = useState([]);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let alive = true;
    setApps(null);
    setSelectedApps([]);
    fetchSendAppsAPI(menuId)
      .then((list) => alive && setApps(list))
      .catch((error) => {
        if (!alive) return;
        setApps([]);
        onError(menuErrorMessage(error, lang) || t.appsFailed);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuId]);

  const count = selectedIds.length;
  const chosen = (apps || []).filter((entry) => selectedApps.includes(entry.key));
  const ready = canSend && !checking && Boolean(action) && count > 0 && chosen.length > 0 && chosen.every((entry) => entry.configured);
  // Only the restaurant's own apps that can receive e-mail are offered; the others are listed as not configured.
  const configuredApps = (apps || []).filter((entry) => entry.configured);
  const unconfiguredApps = (apps || []).filter((entry) => !entry.configured);
  const appOptions = configuredApps.map((entry) => ({ value: entry.key, label: integrationLabel(entry.key, lang) }));
  const chooseApps = (keys) => setSelectedApps(keys.filter((key) => configuredApps.some((entry) => entry.key === key)));

  // Drafts live in Requests: go to the dashboard of the SAME restaurant and highlight what was just created.
  const goToRequests = (requests) => navigate("/auth/lud-dashboard", { state: { storeId, highlightRequestIds: (requests || []).map((request) => request.id), scrollTo: "requests" } });

  // Requests were created (drafts, or sent / failed ones): re-read the per-item sent status and leave selection mode.
  const outcome = useRef(null);
  const outcomeMessage = (results) => (results.every((entry) => entry.state === "sent") ? pt.allSent : pt.someFailed);
  const finish = async (message, type = "success") => {
    setPreviewing(false);
    let sends = null;
    try {
      sends = await fetchAppSendsAPI(menuId);
    } catch {
      sends = null;
    }
    onSent({ sends, message, type });
  };

  return (
    <div className="send-bar" role="region" aria-label={t.action}>
      <div className="send-bar-info">
        <strong>{t.action}</strong>
        <span>{count > 0 ? t.selectedCount(count) : t.selectItems}</span>
        {!checking && <span className="send-bar-counts">{t.counts(totalCount, availableCount, totalCount - availableCount)}</span>}
        <Button type="button" variant="primary" appearance="soft" size="xs" rounded disabled={checking || availableCount === 0} onClick={onSelectAll}>
          {t.selectAll}
        </Button>
        <Button type="button" variant="primary" appearance="soft" size="xs" rounded disabled={count === 0} onClick={onClear}>
          {t.clear}
        </Button>
      </div>
      <div className="send-bar-controls">
        <Select
          label={t.actionLabel}
          value={action}
          placeholder={t.selectAction}
          fullWidth={false}
          options={["add", "update", "delete"].map((value) => ({ value, label: t.actions[value] }))}
          onChange={(event) => onActionChange(event.target.value)}
        />
        <div className="send-bar-apps">
          <SearchCheckList
            options={appOptions}
            selected={selectedApps}
            onChange={chooseApps}
            placeholder={apps && apps.length === 0 ? t.noApps : t.selectApps}
            disabled={appOptions.length === 0}
          />
          <span className="send-bar-app-tools">
            <Button type="button" size="xs" variant="primary" appearance="soft" rounded disabled={appOptions.length === 0 || selectedApps.length === appOptions.length} onClick={() => chooseApps(appOptions.map((option) => option.value))}>
              {t.selectAllApps}
            </Button>
            <Button type="button" size="xs" variant="primary" appearance="soft" rounded disabled={selectedApps.length === 0} onClick={() => setSelectedApps([])}>
              {t.clearApps}
            </Button>
          </span>
          {unconfiguredApps.length > 0 && (
            <small className="send-bar-app-note">
              {unconfiguredApps.map((entry) => integrationLabel(entry.key, lang)).join("، ")} — {t.appNotConfigured}
            </small>
          )}
        </div>
        <Button type="button" variant="primary" appearance="filled" icon={<Send size={16} />} disabled={!ready} title={ready ? undefined : t.sendHint} onClick={() => setPreviewing(true)}>
          {t.send(count)}
        </Button>
        <Button type="button" variant="warning" appearance="soft" icon={<CircleX size={16} />} onClick={onCancel}>
          {t.cancel}
        </Button>
      </div>
      {previewing && (
        <SendToAppPreviewModal
          menuId={menuId}
          apps={selectedApps}
          itemIds={selectedIds}
          action={action}
          onStale={(failure) => onError(t.errors[failure?.code] || menuErrorMessage(failure, lang), failure)}
          onClose={() => (outcome.current ? finish(outcomeMessage(outcome.current), outcome.current.some((entry) => entry.state !== "sent") ? "error" : "success") : setPreviewing(false))}
          onDraftSaved={({ message, requests }) => {
            setPreviewing(false);
            goToRequests(requests);
          }}
          onSent={({ results }) => {
            // the modal stays open on its result step; the bar closes when the user closes it
            outcome.current = results;
          }}
        />
      )}
    </div>
  );
}
